import { useRole } from "hooks/common";
import { useNavigate, useParams } from "react-router-dom";
import { CourseDetailsContent, GroupClassroomContent } from "content";
import { Loader } from "components/Common/Loader";
import { Space } from "components/Common/Space";
import { Button } from "components/Interactive/Button";
import { Heading } from "components/Typography/Heading";
import { Paragraph } from "components/Typography/Paragraph";
import { Text } from "components/Typography/Text";
import { Route } from "constants/routes";
import { TCourse } from "types/api/course";
import { detachCourseFromGroup } from "api/services/courses";
import "./CourseHero.styles.scss";

type TCourseHeroProps = {
  /**
   * Course data to display in `Hero` component.
   */
  course?: TCourse;
  /**
   * If `true` component will display loader with animation.
   *
   * @default false
   */
  loading?: boolean;
};

export const CourseHero: React.FC<TCourseHeroProps> = (props) => {
  const { course, loading = false } = props;
  const { groupId } = useParams();
  const { isUserTeacher } = useRole();

  const navigate = useNavigate();

  const backToGroupClassroomClickHandler = () => {
    if (!groupId) return;
    navigate(Route.App.Study.GROUP_CLASSROOM.construct(groupId));
  };

  const unpinCourseClickHandler = async () => {
    if (!course?.id || !groupId) return;

    try {
      await detachCourseFromGroup(course.id, { groupId: Number(groupId) });
      // Navigate back to group classroom after successful unpinning
      navigate(Route.App.Study.GROUP_CLASSROOM.construct(groupId));
    } catch (error) {
      // Here you could add a toast notification for error handling
    }
  };

  return (
    <section className="nb-sub-group-classroom-course-hero">
      {loading ? (
        <Loader />
      ) : (
        <Space direction="horizontal" justify="start" align="start" fullWidth>
          <div className="nb-sub-group-classroom-course-image">
            <div className="nb-sub-group-classroom-course-poster">
              {course?.imageUrl ? (
                <img
                  src={course.imageUrl}
                  width="171"
                  height="219"
                  alt={`Обкладинка курсу ${course?.name}}`}
                  className="nb-sub-group-classroom-course-picture"
                />
              ) : (
                <span className="nb-sub-group-classroom-course-emoji">🚀</span>
              )}
            </div>
          </div>
          <Space direction="vertical" justify="start" align="start">
            <Heading
              level={2}
              className="nb-sub-group-classroom-course-heading"
            >
              <Text className="nb-sub-group-classroom-course-heading--emoji">
                {CourseDetailsContent.Hero.Content.Heading.EMOJI}
              </Text>
              {course?.name}
            </Heading>
            {course?.description && (
              <Paragraph
                variant={2}
                className="nb-sub-group-classroom-course-paragraph"
              >
                {course.description}
              </Paragraph>
            )}
          </Space>
          <Space
            direction="horizontal"
            size="x-small"
            justify="start"
            align="center"
            className="nb-sub-group-classroom-course-actions"
          >
            <Button
              variant="secondary"
              onClick={backToGroupClassroomClickHandler}
            >
              {GroupClassroomContent.Hero.Action.BACK}
            </Button>
            {isUserTeacher && (
              <Button onClick={unpinCourseClickHandler}>
                {GroupClassroomContent.Hero.Action.UNPIN}
              </Button>
            )}
          </Space>
        </Space>
      )}
    </section>
  );
};
